import React, { useState, useEffect, useRef } from 'react';
import { motion } from "framer-motion";
import { ChevronDown, Facebook, FacebookIcon, Instagram, Linkedin, Mail, MailOpen, MessageSquare, MessageSquareText, Phone, PhoneCall, Send, SendHorizonal, X } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from '../../../firebase';
import {toast} from 'react-toastify'
import emailjs from '@emailjs/browser';
import ReactCountryFlag from 'react-country-flag';

// Country list for the phone dropdown
const COUNTRIES = [
  { name: 'Pakistan', code: 'PK', dial: '+92' },
  { name: 'United States', code: 'US', dial: '+1' },
  { name: 'Canada', code: 'CA', dial: '+1' },
  { name: 'United Kingdom', code: 'GB', dial: '+44' },
  { name: 'United Arab Emirates', code: 'AE', dial: '+971' },
  { name: 'Saudi Arabia', code: 'SA', dial: '+966' },
  { name: 'Qatar', code: 'QA', dial: '+974' },
  { name: 'Kuwait', code: 'KW', dial: '+965' },
  { name: 'Bahrain', code: 'BH', dial: '+973' },
  { name: 'Oman', code: 'OM', dial: '+968' },
  { name: 'India', code: 'IN', dial: '+91' },
  { name: 'Bangladesh', code: 'BD', dial: '+880' },
  { name: 'Afghanistan', code: 'AF', dial: '+93' },
  { name: 'Turkey', code: 'TR', dial: '+90' },
  { name: 'Egypt', code: 'EG', dial: '+20' },
  { name: 'China', code: 'CN', dial: '+86' },
  { name: 'Japan', code: 'JP', dial: '+81' },
  { name: 'South Korea', code: 'KR', dial: '+82' },
  { name: 'Malaysia', code: 'MY', dial: '+60' },
  { name: 'Singapore', code: 'SG', dial: '+65' },
  { name: 'Indonesia', code: 'ID', dial: '+62' },
  { name: 'Australia', code: 'AU', dial: '+61' },
  { name: 'New Zealand', code: 'NZ', dial: '+64' },
  { name: 'Germany', code: 'DE', dial: '+49' },
  { name: 'France', code: 'FR', dial: '+33' },
  { name: 'Italy', code: 'IT', dial: '+39' },
  { name: 'Spain', code: 'ES', dial: '+34' },
  { name: 'Netherlands', code: 'NL', dial: '+31' },
  { name: 'Sweden', code: 'SE', dial: '+46' },
  { name: 'Norway', code: 'NO', dial: '+47' },
  { name: 'Ireland', code: 'IE', dial: '+353' },
  { name: 'Russia', code: 'RU', dial: '+7' },
  { name: 'Brazil', code: 'BR', dial: '+55' },
  { name: 'Mexico', code: 'MX', dial: '+52' },
  { name: 'South Africa', code: 'ZA', dial: '+27' },
  { name: 'Nigeria', code: 'NG', dial: '+234' },
  { name: 'Kenya', code: 'KE', dial: '+254' },
];

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

const OTHER_SERVICE_VALUE = '__other__';

const ContactForm = () => {

    const [firstName, setFirstName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [city, setCity] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    // New state
    const [country, setCountry] = useState(COUNTRIES[0]); // default Pakistan
    const [isCountryOpen, setIsCountryOpen] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const [isOtherService, setIsOtherService] = useState(false);
    const [customService, setCustomService] = useState('');
    const [errors, setErrors] = useState({ email: '', phone: '', service: '' });

    const countryRef = useRef(null);

    // Close country dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (countryRef.current && !countryRef.current.contains(e.target)) {
          setIsCountryOpen(false);
          setCountrySearch('');
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── Validators ──
    const validateEmail = (value) => {
      const v = value.trim();
      if (!v) return 'Email is required.';
      if (!EMAIL_REGEX.test(v)) return 'Please enter a valid email address (e.g. you@example.com).';
      return '';
    };

    const validatePhone = (value, selectedCountry = country) => {
      if (!value) return 'Phone number is required.';
      if (!/^\d+$/.test(value)) return 'Phone number can only contain digits.';
      // Drop a leading 0 (e.g. 0317... -> 317...) when checking length
      const digits = value.replace(/^0+/, '');
      if (selectedCountry.code === 'PK') {
        if (digits.length !== 10) return 'Enter a valid Pakistani number (10 digits, e.g. 3177770287).';
      } else if (digits.length < 6 || digits.length > 14) {
        return 'Enter a valid phone number (6 to 14 digits).';
      }
      return '';
    };

    const validateService = () => {
      if (isOtherService && !customService.trim()) return 'Please enter your service.';
      return '';
    };

    const handleEmailChange = (e) => {
      const value = e.target.value;
      setEmail(value);
      if (errors.email) setErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    };

    // Allow digits only (blocks letters and symbols, also when pasting)
    const handlePhoneChange = (e) => {
      const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 15);
      setPhone(digitsOnly);
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: validatePhone(digitsOnly) }));
    };

    const handlePhoneKeyDown = (e) => {
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
      if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
      if (!/^\d$/.test(e.key)) e.preventDefault();
    };

    const handleServiceChange = (e) => {
      const value = e.target.value;
      if (value === OTHER_SERVICE_VALUE) {
        setIsOtherService(true);
        setCity('');
      } else {
        setCity(value);
      }
    };

    const backToServiceList = () => {
      setIsOtherService(false);
      setCustomService('');
      setCity('');
      setErrors((prev) => ({ ...prev, service: '' }));
    };

    const handleSelectCountry = (c) => {
      setCountry(c);
      setIsCountryOpen(false);
      setCountrySearch('');
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: validatePhone(phone, c) }));
    };

    const filteredCountries = COUNTRIES.filter((c) => {
      const q = countrySearch.trim().toLowerCase();
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q);
    });

const handleSendResults = async (e) => {
  e.preventDefault();

  // Validate before submitting
  const newErrors = {
    email: validateEmail(email),
    phone: validatePhone(phone),
    service: validateService(),
  };
  setErrors(newErrors);
  if (newErrors.email || newErrors.phone || newErrors.service) {
    return;
  }

  setIsLoading(true);

  const finalService = isOtherService ? customService.trim() : city;
  const fullPhone = `${country.dial}${phone.replace(/^0+/, '')}`;

 try {
    const formData = {
      fullName: firstName,
      email: email.trim(),
      phone: fullPhone,
      city: finalService,
      message: message,
      status: "pending",
      createdAt: serverTimestamp(),
    };

   await addDoc(collection(db, "contact_us"), formData);

    await emailjs.send(
      import.meta.env.VITE_EMAILJS_SERVICE_ID,
      import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
      {
        fullName: firstName,
        email: email.trim(),
        phone: fullPhone,
        city: finalService,
        message: message,
        website_name: "Hifah Technologies",
      },
      import.meta.env.VITE_EMAILJS_PUBLIC_KEY
    );

    toast.success("Form submitted successfully!")
    setFirstName('')
    setEmail('')
    setPhone('')
    setCity('')
    setMessage('')
    setCustomService('')
    setIsOtherService(false)
    setCountry(COUNTRIES[0])
    setErrors({ email: '', phone: '', service: '' })

  } catch (err) {
    console.error(err);
    console.error("Error submitting form:", err);
    console.error("Error code:", err.code);
    console.error("Error message:", err.message);
    toast.error("Error submitting form. Please try again or contact support.");
  } finally {
    setIsLoading(false);
  }
};

const navigate = useNavigate();

    return (
        <section className='py-6 mb-20 mont'>
            <div className='px-4 sm:px-16 mont mt-0'>
                <motion.div className='flex flex-col xl:flex-row items-start gap-6'>

                  {/* ── Left: Form ── */}
                  <motion.div className='w-full xl:w-[70%] p-4 rounded-2xl'>
                    <h1 className='text-[#333333] text-[26px] font-semibold'>Send Us Massage</h1>
                    <div className='mt-5 mont font-medium text-[#333333] text-xs sm:text-sm'>

                      <form onSubmit={(e) => handleSendResults(e)} className='w-full' noValidate>

                        {/* Full name and Email */}
                        <div className='flex flex-col sm:flex-row gap-4 mt-6 w-full text-[#333333] font-medium roboto'>
                          <div className='w-full'>
                            <label htmlFor="fullName">Full Name</label>
                            <input
                              type="text"
                              id='fullName'
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              className="w-full mt-1.5 border border-[#E5E5E5] bg-white rounded-[6px] px-4 py-3 text-sm text-[#374151] focus:outline-none focus:border-[#90c769] transition-colors duration-200 appearance-none"
                              placeholder="Full name"
                              required
                            />
                          </div>
                          <div className='w-full'>
                            <label htmlFor="email">Email</label>
                            <input
                              type="email"
                              id='email'
                              value={email}
                              onChange={handleEmailChange}
                              onBlur={() => setErrors((prev) => ({ ...prev, email: validateEmail(email) }))}
                              className={`w-full mt-1.5 border ${errors.email ? 'border-red-500' : 'border-[#E5E5E5]'} bg-white rounded-[6px] px-4 py-3 text-sm text-[#374151] focus:outline-none focus:border-[#90c769] transition-colors duration-200 appearance-none`}
                              placeholder='you@example.com'
                              aria-invalid={!!errors.email}
                              required
                            />
                            {errors.email && (
                              <p className='text-red-500 text-xs mt-1.5 font-normal'>{errors.email}</p>
                            )}
                          </div>
                        </div>

                        {/* Phone and Service */}
                        <div className='flex flex-col sm:flex-row gap-4 mt-6 w-full text-[#333333] font-medium roboto'>
                          <div className='w-full'>
                            <label htmlFor="phone">Phone Number</label>
                            <div className='flex mt-1.5 gap-2'>

                              {/* Country code dropdown */}
                              <div className='relative' ref={countryRef}>
                                <button
                                  type='button'
                                  onClick={() => setIsCountryOpen((prev) => !prev)}
                                  className='flex items-center gap-1.5 border border-[#E5E5E5] bg-white rounded-[6px] px-3 py-3 text-sm text-[#374151] focus:outline-none focus:border-[#90c769] transition-colors duration-200 whitespace-nowrap h-full'
                                  aria-haspopup='listbox'
                                  aria-expanded={isCountryOpen}
                                >
                                  <ReactCountryFlag
                                    countryCode={country.code}
                                    svg
                                    title={country.name}
                                    aria-label={country.name}
                                    style={{ width: '1.4em', height: '1.4em', borderRadius: '2px', objectFit: 'cover' }}
                                  />
                                  <span>{country.dial}</span>
                                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isCountryOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isCountryOpen && (
                                  <div className='absolute z-30 top-full left-0 mt-1 w-64 bg-white border border-[#E5E5E5] rounded-[8px] shadow-lg'>
                                    <div className='p-2 border-b border-[#E5E5E5]'>
                                      <input
                                        type='text'
                                        value={countrySearch}
                                        onChange={(e) => setCountrySearch(e.target.value)}
                                        placeholder='Search country or code'
                                        className='w-full border border-[#E5E5E5] rounded-[6px] px-3 py-2 text-sm text-[#374151] focus:outline-none focus:border-[#90c769]'
                                        autoFocus
                                      />
                                    </div>
                                    <ul className='max-h-56 overflow-y-auto py-1' role='listbox'>
                                      {filteredCountries.length === 0 && (
                                        <li className='px-3 py-2 text-sm text-[#9a9fa8]'>No country found</li>
                                      )}
                                      {filteredCountries.map((c) => (
                                        <li
                                          key={c.code}
                                          role='option'
                                          aria-selected={c.code === country.code}
                                          onClick={() => handleSelectCountry(c)}
                                          className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#F3F4F6] ${c.code === country.code ? 'bg-[#F3F4F6]' : ''}`}
                                        >
                                          <ReactCountryFlag
                                            countryCode={c.code}
                                            svg
                                            title={c.name}
                                            aria-label={c.name}
                                            style={{ width: '1.4em', height: '1.4em', borderRadius: '2px', objectFit: 'cover' }}
                                          />
                                          <span className='flex-1 truncate'>{c.name}</span>
                                          <span className='text-[#6B7280]'>{c.dial}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>

                              {/* Phone number input (digits only) */}
                              <input
                                type="tel"
                                id='phone'
                                value={phone}
                                inputMode='numeric'
                                pattern='[0-9]*'
                                minLength={9}
                                maxLength={15}
                                onChange={handlePhoneChange}
                                onKeyDown={handlePhoneKeyDown}
                                onBlur={() => setErrors((prev) => ({ ...prev, phone: validatePhone(phone) }))}
                                className={`w-full border ${errors.phone ? 'border-red-500' : 'border-[#E5E5E5]'} bg-white rounded-[6px] px-4 py-3 text-sm text-[#374151] focus:outline-none focus:border-[#90c769] transition-colors duration-200 appearance-none`}
                                placeholder='3177770287'
                                aria-invalid={!!errors.phone}
                                required
                              />
                            </div>
                            {errors.phone && (
                              <p className='text-red-500 text-xs mt-1.5 font-normal'>{errors.phone}</p>
                            )}
                          </div>

                          <div className='w-full'>
                            <label htmlFor={isOtherService ? 'customService' : 'city'}>Service</label>

                            {isOtherService ? (
                              /* Custom service input (shown when "Other" is chosen) */
                              <div className='relative'>
                                <input
                                  type='text'
                                  id='customService'
                                  value={customService}
                                  onChange={(e) => {
                                    setCustomService(e.target.value);
                                    if (errors.service) setErrors((prev) => ({ ...prev, service: '' }));
                                  }}
                                  className={`w-full mt-1.5 border ${errors.service ? 'border-red-500' : 'border-[#E5E5E5]'} bg-white rounded-[6px] pl-4 pr-11 py-3 text-sm text-[#374151] focus:outline-none focus:border-[#90c769] transition-colors duration-200 appearance-none`}
                                  placeholder='Type your service'
                                  autoFocus
                                />
                                <button
                                  type='button'
                                  onClick={backToServiceList}
                                  title='Back to service list'
                                  aria-label='Back to service list'
                                  className='absolute right-3 top-[19px] text-[#6B7280] hover:text-[#333333]'
                                >
                                  <X className='w-5 h-5' />
                                </button>
                              </div>
                            ) : (
                              <div className='relative'>
                                <select
                                  id="city"
                                  value={city}
                                  onChange={handleServiceChange}
                                  className={`w-full ${city === '' ? 'text-[#9a9fa8]' : ''} mt-1.5 border border-[#E5E5E5] bg-white rounded-[6px] px-4 py-3 text-sm text-[#374151] focus:outline-none focus:border-[#90c769] transition-colors duration-200 appearance-none`}
                                >
                                  <option value="" disabled>Select Service</option>
                                  <option value="UI/UX Designing">UI/UX Designing</option>
                                  <option value="Web development">Web development</option>
                                  <option value="app development">app development</option>
                                  <option value="digital marketing">digital marketing</option>
                                  <option value="ai solutions">ai solutions</option>
                                  <option value={OTHER_SERVICE_VALUE}>Other</option>
                                </select>
                                <ChevronDown className='w-5 h-5 absolute right-4 top-5 cursor-pointer pointer-events-none' />
                              </div>
                            )}

                            {errors.service && (
                              <p className='text-red-500 text-xs mt-1.5 font-normal'>{errors.service}</p>
                            )}
                          </div>
                        </div>

                        {/* Message */}
                        <div className='w-full text-[#333333] font-medium roboto mt-4 sm:mt-11'>
                          <label htmlFor="message">Message</label>
                          <textarea
                            id="message"
                            value={message}
                            rows={4}
                            onChange={(e) => setMessage(e.target.value)}
                            className='w-full mt-2 resize-none bg-white border border-[#DADDE2] px-4 py-2.5 rounded-lg placeholder:text-[#838383] mont outline-none'
                            placeholder='Tell us about your project..'
                          />
                        </div>

                        <button
                          type='submit'
                          disabled={isLoading}
                          className='mt-4 sm:mt-11 w-full text-white font-medium text-sm lg:text-[17px] cursor-pointer bg-[#4AC3D5] py-[10px] flex justify-center text-center rounded-[8px] disabled:opacity-70 disabled:cursor-not-allowed'
                        >
                          <p className='flex gap-3 items-center'>
                            {isLoading ? 'Loading...' : "Send message"}
                          </p>
                        </button>
                      </form>
                    </div>
                  </motion.div>

                  {/* ── Right: Contact Info ── */}
                  <div className='w-full xl:w-[30%] rounded-[20px] p-5 mt-0 xl:mt-0'>
                    <h3 className='font-semibold text-xl sm:text-2xl text-[#171A1F]'>Need Quick Help?</h3>
                    <p className='font-medium text-sm sm:text-base text-[#475467] mt-2'>
                      Our support team is available during working hours for immediate assistance.
                    </p>

                    {/* Buttons */}
                    <div className='flex flex-col gap-4 mt-6 roboto'>
                      <a href="https://wa.me/923177770287" target="_blank" rel="noopener noreferrer">
                        <button className='w-full bg-[#4AC3D5] font-medium rounded-lg py-2.5 flex items-center justify-center gap-4 text-white'>
                          <MessageSquareText className='w-5 h-5 flex-shrink-0' />
                          Live chat
                        </button>
                      </a>
                      <a href="mailto:hifahtechnologiesofficial@gmail.com" target="_blank" rel="noopener noreferrer">
                        <button className='w-full bg-[#4AC3D5] font-medium rounded-lg py-2.5 flex items-center justify-center gap-4 text-white'>
                          <SendHorizonal className='w-5 h-5 -rotate-45 stroke-2 flex-shrink-0' />
                          Email Support
                        </button>
                      </a>
                    </div>

                    {/* Address */}
                    <a href="https://maps.google.com/?q=Hifah Technologies+Jadoon+Plaza+Phase+2+Abbottabad" target="_blank" rel="noopener noreferrer">
                      <div className='flex items-start gap-3 mt-6 text-[#344054] font-semibold text-sm sm:text-base'>
                        <div className='bg-[#344054]/10 w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md mt-0.5'>
                         <svg
  className="text-[#344054]"
  width="18"
  height="18"
  viewBox="0 0 18 18"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M9 9.375C10.2426 9.375 11.25 8.36764 11.25 7.125C11.25 5.88236 10.2426 4.875 9 4.875C7.75736 4.875 6.75 5.88236 6.75 7.125C6.75 8.36764 7.75736 9.375 9 9.375Z"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  />
  <path
    d="M9 16.5C10.5 13.5 15 11.5637 15 7.5C15 4.18629 12.3137 1.5 9 1.5C5.68629 1.5 3 4.18629 3 7.5C3 11.5637 7.5 13.5 9 16.5Z"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  />
</svg>
                        </div>
                        <span className='break-words mt-1 min-w-0'>Jadoon Plaza Phase 2, Abbottabad</span>
                      </div>
                    </a>

                    {/* Email */}
                    <a href="mailto:hifahtechnologiesofficial@gmail.com" target="_blank" rel="noopener noreferrer">
                      <div className='flex items-start gap-3 mt-4 text-[#344054] font-semibold text-sm sm:text-base'>
                        <div className=' bg-[#344054]/10 w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md mt-0.5'>
                          <MailOpen className='w-4 h-4 text-[#344054]' stroke='#344054' />
                        </div>
                        {/* break-all breaks the long email address across lines */}
                        <span className='break-all mt-1 min-w-0'>hifahtechnologiesofficial@gmail.com</span>
                      </div>
                    </a>

                    {/* Phone */}
                    <a href="http://wa.me/923281223062" target="_blank" rel="noopener noreferrer">
                      <div className='flex items-start gap-3 mt-4 text-[#344054] font-semibold text-sm sm:text-base'>
                        <div className='bg-[#344054]/10 w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md mt-0.5'>
                          <Phone className='w-4 h-4 text-[#344054]' />
                        </div>
                        <span className='break-words mt-1 min-w-0'>+923177770287</span>
                      </div>
                    </a>
                  </div>

                </motion.div>
            </div>
        </section>
    )
}

export default ContactForm